/**
 * Hook for checking whether a meeting has active deals, used by
 * MeetingDetailSection to gate rendering of DealStatusSection.
 *
 * Uses the same React Query cache keys as DealStatusSection so no duplicate
 * network requests are made when both are active in the same render tree.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import type { CrmObject } from '@/types/crm';

import { useAssociations } from '../../hooks/useAssociations';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { filterActiveDeals } from '../utils/meetingPrepTypes';

// Keep these arrays in sync with DealStatusSection so React Query deduplicates.
const DEAL_PROPERTIES = [
  'ahoy_name',
  'ahoy_amount',
  'ahoy_close_date',
] as const;
const DEAL_OWNER_ASSOCIATION_PROPERTIES = ['ahoy_display_name'] as const;
const DEAL_RISK_ASSOCIATION_PROPERTIES = [
  'ahoy_title',
  'ahoy_severity',
  'ahoy_risk_category',
  'ahoy_status',
] as const;
const DEAL_STAGE_ASSOCIATION_PROPERTIES = ['ahoy_name', 'ahoy_order'] as const;

export function useMeetingDeals(
  object: CrmObject,
  enabled = true
): {
  activeDeals: CrmObject[];
  isLoading: boolean;
  isError: boolean;
} {
  const { currentOrg } = useOrg();
  const orgId = object.org_id;

  // Pass `undefined` for orgId/teamId when disabled so internal queries
  // short-circuit without firing network requests.
  const effectiveTeamId = enabled ? currentOrg?.id : undefined;
  const effectiveOrgId = enabled ? currentOrg?.id : undefined;

  const {
    associations: resolvedAssociations,
    isLoading: isAssocLoading,
    error: assocError,
  } = useAssociations(effectiveTeamId, object.associations, {
    sourceEntityTypeName: object.entity_type_name,
  });

  // Read `target_entity_id` directly off the raw association rather than the
  // hydrated `object`. `useAssociations` deliberately skips hydration for
  // `ahoy_meeting` sources (see SKIP_ENSURE_FOR_SOURCE_TYPES in
  // useAssociations.ts), so `assoc.object` is null unless the company is
  // already in the store — relying on it here would suppress the deal fetch.
  const companyEntityId = useMemo(() => {
    const companyAssoc = resolvedAssociations.find(
      a => a.association_type_name === 'ahoy_meeting_to_company'
    );
    return companyAssoc?.target_entity_id ?? null;
  }, [resolvedAssociations]);

  // Skip the stages/schema fetch entirely for meetings with no company
  // association — without a company there can be no active deals to filter,
  // so the stage data is unused.
  const stagesOrgId =
    effectiveOrgId && companyEntityId ? effectiveOrgId : undefined;

  const {
    getStageByEntityId,
    stageTypeNames,
    isLoading: isStagesLoading,
    error: stagesError,
  } = usePipelineStages(stagesOrgId, 'ahoy_deal');

  const dealQueryOptions = useMemo(
    () =>
      crmObjectListSentinelQuery(
        orgId,
        'ahoy_deal',
        {
          associated_with_entity_id: companyEntityId ?? undefined,
          properties: [...DEAL_PROPERTIES],
        },
        {
          include_associations: {
            ahoy_deal_to_owner: {
              properties: [...DEAL_OWNER_ASSOCIATION_PROPERTIES],
            },
            ahoy_deal_to_risk: {
              properties: [...DEAL_RISK_ASSOCIATION_PROPERTIES],
            },
            ahoy_deal_to_deal_pipeline_stage: {
              properties: [...DEAL_STAGE_ASSOCIATION_PROPERTIES],
            },
          },
        }
      ),
    [orgId, companyEntityId]
  );

  const dealsQuery = useQuery({
    ...dealQueryOptions,
    enabled: enabled && !!companyEntityId,
  });
  const list = useStoreBackedList(dealsQuery);

  const activeDeals = useMemo(
    () => filterActiveDeals(list.results, getStageByEntityId, stageTypeNames),
    [list.results, getStageByEntityId, stageTypeNames]
  );

  return {
    activeDeals,
    isLoading: isAssocLoading || isStagesLoading || list.isLoading,
    isError: !!list.error || !!assocError || !!stagesError,
  };
}
